import { useState } from "react";
import { Layout, Button, theme } from "antd";
import MenuList from "./Components/Sidebar/MenuList";
import { MenuUnfoldOutlined, MenuFoldOutlined } from '@ant-design/icons';
import { ToggleThemeButton } from "./Components/ToggleThemeButton";
import { Outlet, Routes, Route } from 'react-router-dom';
import Students from "./Components/Administrations/StudentsList";
import UsersTable from './Components/Administrations/Users';
import AddUser from './Components/Forms/AddUser';
import UpdateUser from './Components/Forms/UpdateUser';
import VideoGallery from './Components/Administrations/VideoGallery';
import CourseTable from './Components/Course/CourseList';
import AddCourse from './Components/Forms/AddCourse';
import EditCourse from './Components/Forms/UpdateCourse';
import VideosTable from './Components/Administrations/VideoTable';
import EditVideo from './Components/Forms/EditVideo';
import PackageForm from "./Components/Forms/PackageForm";
import { FiLogOut } from "react-icons/fi";
import config from "./config";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PackagesTable from "./Components/Administrations/Packages";
import QuestionsGrid from "./Components/Administrations/QuestionsGrid";
import QuestionForm from "./Components/Forms/QuestionForm";
import QuestionFormPage from "./Components/Forms/QuestionFormPage";
import ExamsGrid from "./Components/Administrations/ٍExamsGrid"
import ExamForm from "./Components/Forms/ExamForm";
import ExamBankGrid from "./Components/Administrations/ExamBankGrid"
import ExamBankForm from "./Components/Forms/ExmaBankForm";
import ProtectedRoute from "./Components/Guards/ProtectedRoute";
import Unauthorized from "./Components/Unauthorized/Unauthorized";
import { PERMISSIONS } from "./constants/permissions";
import RolesGrid from "./Components/Roles/RolesGrid";
import RoleForm from "./Components/Roles/RoleForm";
import RoleView from "./Components/Roles/RoleView";
import ActivityLogsGrid from "./Components/Administrations/ActivityLogsGrid";
import DatabaseBackups from "./Components/Administrations/DatabaseBackups";
import DashboardRedirect from "./Components/Dashboard/DashboardRedirect";
import { useAuth } from "./contexts/AuthContext";
import { Navigate } from "react-router-dom";
const { Header, Sider, Content } = Layout;
function App() {
  const [darkTheme, setDarkTheme] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const { logout, loading, isAuthenticated } = useAuth();
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const handleLogout = async () => {
    try {
      await axios.post(`${config.apiBaseUrl}/logout`, {}, {
        headers: {
          "Content-Type": "application/json",
          "HeaderKey": config.HeaderKey
        },
        withCredentials: true,
      });

      // Clear auth state
      logout();

      // Redirect to login page after logout
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
      // Still logout locally even if server request fails
      logout();
      navigate("/");
    }
  };

  const toggleThem = () => {
    setDarkTheme(!darkTheme);
  };

  const isMobile = window.innerWidth < 768;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-custom">در حال بارگذاری...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <Layout className="relative">
      <Layout style={{ marginRight: collapsed ? 80 : 200, transition: ' margin 0.42s' }}>
        <Header className="flex justify-end items-center px-4" style={{ background: colorBgContainer }}>
          <Button type='text' onClick={() => setCollapsed(!collapsed)} icon={collapsed ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />} />
        </Header>
        <Content className="p-4">
          <Routes>
            <Route index element={<DashboardRedirect />} />

            {/* Unauthorized page */}
            <Route path="unauthorized" element={<Unauthorized />} />

            {/* Users routes - require Users.View */}
            <Route
              path="users"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.USERS_VIEW}>
                  <UsersTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="students"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.USERS_VIEW}>
                  <Students />
                </ProtectedRoute>
              }
            />
            <Route
              path="users/adduser"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.USERS_CREATE}>
                  <AddUser />
                </ProtectedRoute>
              }
            />
            <Route
              path="users/updateuser"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.USERS_EDIT}>
                  <UpdateUser />
                </ProtectedRoute>
              }
            />

            {/* Gallery - requires Contents.View */}
            <Route
              path="gallery"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.CONTENTS_VIEW}>
                  <VideoGallery />
                </ProtectedRoute>
              }
            />

            {/* Courses routes - require Courses.View */}
            <Route
              path="courses"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.COURSES_VIEW}>
                  <CourseTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="courses/addcourse"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.COURSES_CREATE}>
                  <AddCourse />
                </ProtectedRoute>
              }
            />
            <Route
              path="courses/updatecourse"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.COURSES_EDIT}>
                  <EditCourse />
                </ProtectedRoute>
              }
            />

            {/* Videos/Content routes - require Contents.View */}
            <Route
              path="videos"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.CONTENTS_VIEW}>
                  <VideosTable type={0} />
                </ProtectedRoute>
              }
            />
            <Route
              path="pdfs"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.CONTENTS_VIEW}>
                  <VideosTable type={1} />
                </ProtectedRoute>
              }
            />
            <Route
              path="voices"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.CONTENTS_VIEW}>
                  <VideosTable type={2} />
                </ProtectedRoute>
              }
            />
            <Route
              path="videos/edit"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.CONTENTS_UPLOAD}>
                  <EditVideo />
                </ProtectedRoute>
              }
            />

            {/* Packages routes - require Packages.View */}
            <Route
              path="packages"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.PACKAGES_VIEW}>
                  <PackagesTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="package/form"
              element={
                <ProtectedRoute requiredPermissions={[PERMISSIONS.PACKAGES_CREATE, PERMISSIONS.PACKAGES_EDIT]}>
                  <PackageForm />
                </ProtectedRoute>
              }
            />

            {/* Questions routes - require Questions.View */}
            <Route
              path="questions"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.QUESTIONS_VIEW}>
                  <QuestionsGrid />
                </ProtectedRoute>
              }
            />
            <Route
              path="questions/form"
              element={
                <ProtectedRoute requiredPermissions={[PERMISSIONS.QUESTIONS_CREATE, PERMISSIONS.QUESTIONS_EDIT]}>
                  <QuestionFormPage />
                </ProtectedRoute>
              }
            />

            {/* Exams routes - require Exams.View */}
            <Route
              path="exams"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.EXAMS_VIEW}>
                  <ExamsGrid />
                </ProtectedRoute>
              }
            />
            <Route
              path="exams/form"
              element={
                <ProtectedRoute requiredPermissions={[PERMISSIONS.EXAMS_CREATE, PERMISSIONS.EXAMS_EDIT]}>
                  <ExamForm />
                </ProtectedRoute>
              }
            />

            {/* ExamBanks routes - require ExamBanks.View */}
            <Route
              path="ExamBanks"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.EXAMBANKS_VIEW}>
                  <ExamBankGrid />
                </ProtectedRoute>
              }
            />
            <Route
              path="ExamBanks/form"
              element={
                <ProtectedRoute requiredPermissions={[PERMISSIONS.EXAMBANKS_CREATE, PERMISSIONS.EXAMBANKS_EDIT]}>
                  <ExamBankForm />
                </ProtectedRoute>
              }
            />

            {/* Roles Management routes - require Roles.View */}
            <Route
              path="roles"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.ROLES_VIEW}>
                  <RolesGrid />
                </ProtectedRoute>
              }
            />
            <Route
              path="roles/form"
              element={
                <ProtectedRoute requiredPermissions={[PERMISSIONS.ROLES_CREATE, PERMISSIONS.ROLES_EDIT]}>
                  <RoleForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="roles/view"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.ROLES_VIEW}>
                  <RoleView />
                </ProtectedRoute>
              }
            />

            {/* Activity Logs - require Logs.View */}
            <Route
              path="logs"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.LOGS_VIEW}>
                  <ActivityLogsGrid />
                </ProtectedRoute>
              }
            />

            {/* Database backups - require Backups.View. Download is enforced again by the API. */}
            <Route
              path="backups"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.BACKUPS_VIEW}>
                  <DatabaseBackups />
                </ProtectedRoute>
              }
            />
          </Routes>
        </Content>
      </Layout>
      <Sider collapsed={collapsed} collapsible trigger={null} theme={darkTheme ? 'dark' : 'light'} className="h-screen fixed !right-0 !left-auto z-50">
        <MenuList darkTheme={darkTheme} />
        <div className='absolute bottom-[70px] left-[15px] flex items-center justify-center'>
          <Button onClick={handleLogout}>
             {<FiLogOut /> }
          </Button>
        </div>

        <ToggleThemeButton darkTheme={darkTheme} isMobile={isMobile} toggleTheme={toggleThem} />
      </Sider>
    </Layout>
  );
}

export default App;
